// Embodied chapter rules. No renderer or DOM dependencies: both renderers and
// keyboard/pointer input share these values. Units: island metres and seconds.
import {SURFACE_MIN,SURFACE_MAX} from './interaction-tuning.js';
import {moodProfile} from './mood-system.js';
import {gravityOnTerrain} from './physics.js';
export const INTERACTION_REVISION = 'lived-mood-recovery-r8';
export const clamp01 = n => Math.max(0, Math.min(1, n));
const mix = (a, b, t) => a + (b - a) * t;
const smooth = (a, b, x) => { const t = clamp01((x-a)/(b-a)); return t*t*(3-2*t); };
const neutral = {mass:1,response:28,maxSpeed:2.6,damping:9.5,squash:1,bounce:0,phase:0,steering:1,restless:0,cracks:0};
// A broad rough/high-resistance band still crosses the playable area, but
// R6 removes the visible row of raised sand ribs. Discomfort now comes from
// resistance, quicksand, the continuously shifting terrain and balance tilt.
export function discomfortAt(x,z){
 const cx=.22*Math.sin(z*1.35),u=x-cx;
 const envelope=(1-smooth(1.05,1.75,Math.abs(u)))*(1-smooth(3.18,3.75,Math.abs(z)));
 return {roughness:envelope,height:0};
}
// A held, distant pointer is still a pull, not a pause. Account for both
// pointer travel and the remaining pull on the ball (world units / second).
export function dragIntentSpeed(targetX,targetZ,previousX,previousZ,ballX,ballZ,dt){
 const travel=Math.hypot(targetX-previousX,targetZ-previousZ)/Math.max(dt,.001);
 const pull=Math.min(3,Math.hypot(targetX-ballX,targetZ-ballZ)*2.2);
 return Math.max(travel,pull);
}
export function createChapterState(){return {...neutral,name:'Balanced',strength:0,adaptation:0,roughness:0,quicksand:0,condition:0,effort:0,shake:0,hop:0,sx:1,sy:1,sz:1,phase:0,inputSpeed:0,time:0};}
export function updateChapterState(s,{mode,x,z,speed=0,inputSpeed=0,dx=0,dz=0,dt,time=0,reduced=false,mood=0,quicksand=0}){
 const q=moodProfile(mood),a=1-Math.exp(-dt*3.2);
 for(const k of ['mass','response','maxSpeed','damping','squash','bounce','steering','restless','cracks','condition'])s[k]=mix(s[k],q[k],a);
 s.name=q.label;s.strength=Math.abs(mood);s.time=time;
 s.inputSpeed=mix(s.inputSpeed,Math.min(9,inputSpeed),1-Math.exp(-dt*6));
 const field=mode==='discomfort'?discomfortAt(x,z):{roughness:0};
 s.quicksand=mix(s.quicksand,mode==='discomfort'?quicksand:0,1-Math.exp(-dt*6));
 s.roughness=mix(s.roughness,Math.max(field.roughness,s.quicksand),1-Math.exp(-dt*8));
 const along=Math.abs(dz)/(Math.hypot(dx,dz)||1),gentle=1-smooth(.55,1.6,s.inputSpeed);
 // Local surface adaptation changes traction only. It never changes mood or event deltas.
 if(mode==='discomfort'&&s.roughness>.12)s.adaptation=clamp01(s.adaptation+dt*s.roughness*(.38*gentle+.16*along-.08*(1-gentle)));
 else s.adaptation=Math.max(0,s.adaptation-dt*.025);
 s.effort=s.roughness*(1-s.adaptation*.6)*(.3+.7*smooth(.5,2.4,s.inputSpeed))*(1-along*.35);
 s.shake=reduced?0:mix(s.shake,Math.max(q.tremble,s.effort*.010),a);s.hop=0;
 s.sx=mix(s.sx,q.scale,a);s.sz=s.sx;s.sy=s.sx*(1-(mode==='discomfort'?s.effort*.025:0));
 return s;
}
// The same controller is used by drag and keyboard, so physical feedback
// cannot be bypassed with arrow keys. Unlike a direct position lerp it has a
// finite speed and inertial acceleration (perceived mass).
export function advanceGuided(state,target,dt,feel,{mode='paths',heightAt=()=>0,pitch=0,roll=0,bound=3.42}={}){
 const steps=Math.max(1,Math.ceil(dt/(1/120))),h=dt/steps;
 for(let i=0;i<steps;i++){
  const dx=target.x-state.x,dz=target.z-state.z,d=Math.hypot(dx,dz);
  const along=Math.abs(dz)/(d||1),resistance=mode==='discomfort'?1+feel.roughness*((1-feel.adaptation)*2.6+feel.effort*1.4)*(1-along*.52)+feel.quicksand*7.5:1;
  const cap=feel.maxSpeed/resistance,wanted=Math.min(cap,d*feel.response);
  const vx=d>.00001?dx/d*wanted:0,vz=d>.00001?dz/d*wanted:0;
  // In Discomfort the hand guides; it does not cancel gravity. Counter-tilt matters.
  const authority=mode==='discomfort'?7.5:26;
  const follow=1-Math.exp(-h*authority*(feel.steering??1)/Math.sqrt(feel.mass)/Math.sqrt(resistance));
  if(mode==='discomfort'){
   // A finite hand force: a steep slope can exceed it, so balancing cannot be bypassed by holding a pointer.
   let fx=(vx-state.vx)*follow,fz=(vz-state.vz)*follow;
   const force=Math.hypot(fx,fz),budget=1.5/Math.sqrt(feel.mass)/(1+feel.quicksand*.3)*h;
   if(force>budget){fx*=budget/force;fz*=budget/force;}
   state.vx+=fx;state.vz+=fz;
  }else{state.vx=mix(state.vx,vx,follow);state.vz=mix(state.vz,vz,follow);}
  if(mode==='resilience'||mode==='discomfort'){
   const hx=(heightAt(state.x+.045,state.z)-heightAt(state.x-.045,state.z))/.09;
   const hz=(heightAt(state.x,state.z+.045)-heightAt(state.x,state.z-.045))/.09;
   if(mode==='discomfort'){const gravity=gravityOnTerrain(pitch,roll,hx,hz);state.vx+=gravity.x*h;state.vz+=gravity.z*h;}
   else {state.vx-=(hx*1.6+Math.sin(roll)*5)*h;state.vz+=(Math.sin(pitch)*5-hz*1.6)*h;}
  }
  if(mode==='discomfort'){
   const speed=Math.hypot(state.vx,state.vz),limit=feel.maxSpeed*1.35/(1+feel.quicksand*2);
   if(speed>limit){state.vx*=limit/speed;state.vz*=limit/speed;}
  }
  // Overexcitement is quicker but has less steering authority and a small
  // sideways wobble while moving, rather than a fake visual shake alone.
  if(feel.restless>0&&Math.hypot(state.vx,state.vz)>.1){
   const v=Math.hypot(state.vx,state.vz),side=Math.sin((feel.time||0)*3.4)*feel.restless*h;
   state.vx+=-state.vz/v*side;state.vz+=state.vx/v*side;
  }
  state.x+=state.vx*h;state.z+=state.vz*h;
  state.x=Math.max(-bound,Math.min(bound,state.x));state.z=Math.max(-bound,Math.min(bound,state.z));
 }
 return state;
}
// Separate temporary terrain from saved grooves. A returning landscape must
// never erase a path, or bake its temporary pressure into localStorage.
export class RecoveringTerrain {
 constructor(n,size,base){
  if(base.length!==n*n)throw new Error('Invalid terrain dimensions');
  this.n=n;this.size=size;this.base=base;this.surface=new Float32Array(base);
  this.time=0;this.dynamic=new Float32Array(base.length*5);this.patches=[];this.ridge=new Float32Array(base.length);this.ridgeBlend=0;this.energy=0;this.revision=0;
  for(let z=0;z<n;z++)for(let x=0;x<n;x++){
   const px=(x/(n-1)-.5)*size,pz=(z/(n-1)-.5)*size,i=z*n+x;
   this.ridge[i]=discomfortAt(px,pz).height;
   const edge=1-smooth(2.8,3.65,Math.max(Math.abs(px),Math.abs(pz)));
   this.dynamic[i*5]=Math.sin(px*.90+pz*.48)*edge;
   this.dynamic[i*5+1]=Math.cos(px*.90+pz*.48)*edge;
   this.dynamic[i*5+2]=Math.sin(pz*1.2-px*.38)*edge;
   this.dynamic[i*5+3]=Math.cos(pz*1.2-px*.38)*edge;
   this.dynamic[i*5+4]=Math.exp(-(px*px+pz*pz)/5.5)*edge;
  }
 }
 reset(){this.base.fill(0);this.surface.fill(0);this.time=0;this.patches=[];this.ridgeBlend=0;this.energy=0;this.revision++;}
 disturb(x,z,{kind='press',strength=1}={}){
  const n=this.n,cell=this.size/(n-1),sigma=.77,reach=2.05;
  const cx=(x/this.size+.5)*(n-1),cz=(z/this.size+.5)*(n-1),r=Math.ceil(reach/cell);
  const ids=[],values=[];
  for(let iz=Math.max(0,Math.floor(cz)-r);iz<=Math.min(n-1,Math.ceil(cz)+r);iz++)for(let ix=Math.max(0,Math.floor(cx)-r);ix<=Math.min(n-1,Math.ceil(cx)+r);ix++){
   const px=(ix/(n-1)-.5)*this.size,pz=(iz/(n-1)-.5)*this.size;
   const d=Math.hypot(px-x,pz-z),edge=1-smooth(3.15,3.75,Math.max(Math.abs(px),Math.abs(pz)));
   if(d>reach||edge===0)continue;
   const bowl=Math.exp(-d*d/(sigma*sigma)),rim=Math.exp(-(((d-1.04)/.33)**2));
   const v=(kind==='lift'?.34*bowl:(-.185*bowl+.092*rim))*edge*strength;
   ids.push(iz*n+ix);values.push(v);
  }
  this.patches.push({ids:new Uint32Array(ids),values:new Float32Array(values),amount:1,velocity:0,age:0});
  if(this.patches.length>6)this.patches.shift();
  this.revision++;
 }
 step(dt,mode,{reduced=false}={}){
  const wasActive=this.patches.length,previousBlend=this.ridgeBlend;this.time+=dt;
  this.ridgeBlend=mix(this.ridgeBlend,mode==='discomfort'?1:0,1-Math.exp(-dt*3.2));
  if(mode!=='discomfort'&&this.ridgeBlend<.0001)this.ridgeBlend=0;
  this.energy=0;
  for(const p of this.patches){
   p.age+=dt;
   if(reduced)p.amount=Math.exp(-p.age/1.6);
   else { const steps=Math.max(1,Math.ceil(dt*120)),h=dt/steps;
    for(let i=0;i<steps;i++){p.velocity+=(-2.65*p.amount-2.62*p.velocity)*h;p.amount+=p.velocity*h;}
   }
   this.energy=Math.max(this.energy,Math.abs(p.amount));
  }
  this.patches=this.patches.filter(p=>p.age<12&&(Math.abs(p.amount)>.001||Math.abs(p.velocity)>.002));
  this.surface.set(this.base);
  if(this.ridgeBlend){
   const t=this.time*(reduced?.45:1),ca=Math.cos(t*.57),sa=Math.sin(t*.57),cb=Math.cos(t*.43),sb=Math.sin(t*.43),bowl=.26+.10*Math.sin(t*.24);
   for(let i=0;i<this.surface.length;i++){
    const j=i*5,waves=.28*(this.dynamic[j]*ca+this.dynamic[j+1]*sa)+.18*(this.dynamic[j+2]*cb-this.dynamic[j+3]*sb);
    this.surface[i]+=(this.ridge[i]*1.22+waves-this.dynamic[j+4]*bowl)*this.ridgeBlend*(reduced?.7:1);
   }
  }
  for(const p of this.patches)for(let j=0;j<p.ids.length;j++)this.surface[p.ids[j]]+=p.values[j]*p.amount;
  const changed=!!(wasActive||this.patches.length||this.ridgeBlend>0||Math.abs(this.ridgeBlend-previousBlend)>.000015);
  if(changed){
   for(let i=0;i<this.surface.length;i++)this.surface[i]=Math.max(SURFACE_MIN,Math.min(SURFACE_MAX,this.surface[i]));
   this.revision++;
  }
  return changed;
 }
}
